import { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, TextField, Button, Typography, CircularProgress,
    Alert, Divider, InputAdornment, Chip, Stack
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getInventoryItems, createInventoryLog } from "./InventoryApi";

const CATEGORIES = ["all", "housekeeping", "maintenance", "front_desk", "food_beverage", "safety"];

// ── Inner: Single Item Adjustment ───────────────────────
const AdjustItemDialog = ({ open, item, onClose, onSuccess }) => {
    const [actualCount, setActualCount] = useState("");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const difference = actualCount !== "" ? Number(actualCount) - Number(item?.current_stock ?? 0) : null;
    const adjustType = difference > 0 ? "IN" : "OUT";
    const adjustQty = difference !== null ? Math.abs(difference) : 0;

    const handleClose = () => {
        setActualCount("");
        setNotes("");
        setError("");
        onClose();
    };

    const handleSubmit = async () => {
        if (actualCount === "" || isNaN(actualCount) || Number(actualCount) < 0) {
            setError("Please enter a valid actual count.");
            return;
        }
        if (difference === 0) {
            setError("Actual count matches system stock. No adjustment needed.");
            return;
        }
        if (!notes.trim()) {
            setError("Please provide a reason for the adjustment.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            await createInventoryLog({
                item_id: item.id,
                type: adjustType,
                quantity: adjustQty,
                description: `Adjustment - ${notes.trim()}`,
            });
            onSuccess?.();
            handleClose();
        } catch (err) {
            setError("Something went wrong. Please try again.");
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>
                Adjust — {item?.name}
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Box bgcolor="#f5f5f5" borderRadius={1} p={1.5}>
                        <Typography variant="body2" color="text.secondary" textTransform="capitalize">
                            {item?.category?.replace("_", " ")}
                        </Typography>
                        <Typography variant="subtitle1">
                            System Stock: <strong>{item?.current_stock ?? "—"}</strong>
                        </Typography>
                    </Box>

                    <TextField
                        label="Actual count on shelf"
                        value={actualCount}
                        onChange={(e) => setActualCount(e.target.value)}
                        size="small"
                        type="number"
                        fullWidth
                        autoFocus
                    />

                    {/* Live difference indicator */}
                    {difference !== null && difference !== 0 && (
                        <Box
                            bgcolor={difference > 0 ? "#e8f5e9" : "#fce4ec"}
                            borderRadius={1}
                            p={1.5}
                        >
                            <Typography
                                variant="body2"
                                fontWeight={700}
                                color={difference > 0 ? "success.main" : "error.main"}
                            >
                                {difference > 0
                                    ? `+${difference} will be added to stock`
                                    : `${difference} will be removed from stock`}
                            </Typography>
                        </Box>
                    )}

                    {difference === 0 && actualCount !== "" && (
                        <Alert severity="success">Stock matches. No adjustment needed.</Alert>
                    )}

                    <TextField
                        label="Reason (required)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="e.g. Physical count mismatch, damaged items removed"
                    />

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || difference === 0 || difference === null || !notes.trim()}
                >
                    {loading ? <CircularProgress size={18} /> : "Submit Adjustment"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Outer: Items List + Search ───────────────────────────
const AdjustStockDialog = ({ open, onClose, onSuccess }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("all");
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        if (open) fetchItems();
    }, [open]);

    const fetchItems = async () => {
        setLoading(true);
        const data = await getInventoryItems();
        setItems(data || []);
        setLoading(false);
    };

    const handleAdjustSuccess = () => {
        fetchItems();       // refresh list stock levels
        onSuccess?.();      // notify parent
    };

    const filteredItems = items.filter((item) => {
        const matchSearch = item.name.toLowerCase().includes(search.toLowerCase());
        const matchCategory = category === "all" || item.category === category;
        return matchSearch && matchCategory;
    });

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Adjust Stock</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>

                        {/* Search */}
                        <TextField
                            placeholder="Search item..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            fullWidth
                            autoFocus
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Category Filter */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {CATEGORIES.map((cat) => (
                                <Chip
                                    key={cat}
                                    label={cat.replace("_", " ").replace(/^\w/, c => c.toUpperCase())}
                                    onClick={() => setCategory(cat)}
                                    variant={category === cat ? "filled" : "outlined"}
                                    color={category === cat ? "primary" : "default"}
                                    size="small"
                                    sx={{ textTransform: "capitalize" }}
                                />
                            ))}
                        </Stack>

                        <Divider />

                        {/* Items List */}
                        {loading ? (
                            <Box display="flex" justifyContent="center" py={3}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : filteredItems.length === 0 ? (
                            <Typography color="text.secondary" textAlign="center" py={2}>
                                No items found.
                            </Typography>
                        ) : (
                            <Box display="flex" flexDirection="column" gap={1}>
                                {filteredItems.map((item) => (
                                    <Box
                                        key={item.id}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        p={1.5}
                                        borderRadius={1}
                                        bgcolor={item.current_stock <= item.min_stock ? "#fff3e0" : "#fafafa"}
                                        border="1px solid"
                                        borderColor={item.current_stock <= item.min_stock ? "warning.light" : "divider"}
                                    >
                                        <Box>
                                            <Typography variant="body1" fontWeight={600}>
                                                {item.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" textTransform="capitalize">
                                                {item.category.replace("_", " ")} · Stock:&nbsp;
                                                <Typography
                                                    component="span"
                                                    fontWeight={700}
                                                    color={item.current_stock <= item.min_stock ? "warning.main" : "inherit"}
                                                >
                                                    {item.current_stock}
                                                </Typography>
                                                {item.current_stock <= item.min_stock && " ⚠️"}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setSelectedItem(item)}
                                        >
                                            Adjust
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Nested adjustment dialog */}
            <AdjustItemDialog
                open={!!selectedItem}
                item={selectedItem}
                onClose={() => setSelectedItem(null)}
                onSuccess={handleAdjustSuccess}
            />
        </>
    );
};

export default AdjustStockDialog;